import type { FileInfo, PaperFileData } from "@/stores/slices/lib/paperTypes";
import { mapUploadSettled, UPLOAD_CHECK_BATCH_SIZE, UPLOAD_CONCURRENCY } from "./upload-concurrency";

export type UploadTaskResult = {
  code: number;
  message?: string;
  paper?: PaperFileData | null;
  isDuplicate?: boolean;
};

export type UploadAllTasksCompleteParams = {
  balanceInsufficient: boolean;
  uploadLimitReached: boolean;
};

export type UploadTaskCallbacks = {
  onTaskStart?: (taskId: string, file: File, fileInfo: FileInfo) => void;
  onProgress?: (taskId: string, progress: number) => void;
  onTaskComplete?: (taskId: string, result: UploadTaskResult) => void;
  onAllTasksComplete?: (params: UploadAllTasksCompleteParams) => void;
};

export type UploadExistDataItem = {
  existed: boolean;
  userExisted: boolean;
  subjectExisted: boolean;
  uploadUrl: string;
};

type UploadResponse<T> = { code: number; data?: T; message?: string };
type UploadEntry = { index: number; id: string; file: File; fileInfo: FileInfo };

interface UploadBatchOptions {
  files: File[];
  taskIds: string[];
  filesInfo?: FileInfo[];
  subjectId: string;
  maxFileSize: number;
  concurrency?: number;
  callbacks?: UploadTaskCallbacks;
}

export interface UploadBatchDependencies {
  getFileInfo: (file: File) => Promise<FileInfo>;
  checkFiles: (files: FileInfo[]) => Promise<UploadResponse<UploadExistDataItem[]>>;
  uploadFile: (url: string, file: File, onProgress: (progress: number) => void) => Promise<void>;
  bindFile: (file: FileInfo) => Promise<UploadResponse<PaperFileData[]> | null>;
}

const duplicateCodes = new Set([-3, -4, -5, -6]);

/** A failed file or metadata chunk always settles; later chunks keep running. */
export async function runPaperUploadBatch(options: UploadBatchOptions, dependencies: UploadBatchDependencies) {
  const { files, taskIds, filesInfo, subjectId, maxFileSize, callbacks = {} } = options;
  const concurrency = Math.min(options.concurrency ?? UPLOAD_CONCURRENCY, UPLOAD_CONCURRENCY);
  const results = new Map<number, UploadTaskResult>();
  const firstByHash = new Map<string, UploadEntry>();
  const summary: UploadAllTasksCompleteParams = { balanceInsufficient: false, uploadLimitReached: false };
  const complete = (index: number, result: UploadTaskResult) => {
    if (results.has(index)) return;
    results.set(index, result);
    if (result.code === 5001) summary.balanceInsufficient = true;
    if (result.code === 30009) summary.uploadLimitReached = true;
    callbacks.onTaskComplete?.(taskIds[index], { ...result });
  };

  for (let offset = 0; offset < files.length; offset += UPLOAD_CHECK_BATCH_SIZE) {
    const chunk = files.slice(offset, offset + UPLOAD_CHECK_BATCH_SIZE);
    const prepared = await mapUploadSettled(chunk, concurrency, async (file, localIndex) => {
      const index = offset + localIndex;
      if (file.size > maxFileSize * 1024 * 1024) {
        complete(index, { code: -10 });
        return null;
      }
      const fileInfo = filesInfo?.length === files.length
        ? filesInfo[index]
        : await dependencies.getFileInfo(file);
      return { index, id: taskIds[index], file, fileInfo };
    });

    const unique: UploadEntry[] = [];
    const duplicates: { entry: UploadEntry; first: UploadEntry }[] = [];
    prepared.forEach((result, localIndex) => {
      if (result.status === "rejected") {
        complete(offset + localIndex, { code: -2 });
        return;
      }
      const entry = result.value;
      if (!entry) return;
      callbacks.onTaskStart?.(entry.id, entry.file, entry.fileInfo);
      const first = firstByHash.get(entry.fileInfo.id);
      if (first) {
        duplicates.push({ entry, first });
      } else {
        firstByHash.set(entry.fileInfo.id, entry);
        unique.push(entry);
      }
    });

    if (unique.length) {
      try {
        const checked = await dependencies.checkFiles(unique.map((entry) => entry.fileInfo));
        if (checked.code !== 0 || !Array.isArray(checked.data)) {
          unique.forEach((entry) => complete(entry.index, { code: checked.code || -2 }));
        } else {
          await mapUploadSettled(unique, concurrency, async (entry, index) => {
            try {
              const existing = checked.data?.[index];
              if (!existing) {
                complete(entry.index, { code: -2 });
                return;
              }
              if (!existing.existed && !existing.userExisted) {
                if (!existing.uploadUrl) throw new Error("Missing upload URL");
                await dependencies.uploadFile(existing.uploadUrl, entry.file, (progress) => {
                  callbacks.onProgress?.(entry.id, progress);
                });
              }
              const bound = await dependencies.bindFile(entry.fileInfo);
              if (bound?.code !== 0) {
                complete(entry.index, { code: bound?.code ?? -2 });
                return;
              }
              const code = existing.userExisted
                ? subjectId ? existing.subjectExisted ? -6 : -5 : -4
                : 0;
              complete(entry.index, { code, paper: bound.data?.[0] ?? null, isDuplicate: code !== 0 });
            } catch {
              complete(entry.index, { code: -2 });
            }
          });
        }
      } catch {
        unique.forEach((entry) => complete(entry.index, { code: -2 }));
      }
    }

    duplicates.forEach(({ entry, first }) => {
      const original = results.get(first.index);
      complete(entry.index, original && (original.code === 0 || duplicateCodes.has(original.code))
        ? { code: -3, paper: original.paper, isDuplicate: true }
        : { code: original?.code ?? -2 });
    });
  }

  callbacks.onAllTasksComplete?.(summary);
  return summary;
}
